import React, { useEffect, useState } from 'react';
import axios from 'axios';
import logo from '../assets/Logo_laboratorio_simple_verde-removebg-preview.png';
import { useAuth } from '../context/AuthContext';
import UserMenu from '../components/UserMenu';
import ErrorBanner from '../components/ErrorBanner';
import { AnalisisItem, GrupoResultados, misResultados, descargarPdf } from '../api/analisisApi';
import { nombreArchivoResultado } from '../utils/pdfFilename';
import { ApiError } from '../types/auth.types';
import styles from './MisResultadosPage.module.css';

function formatFecha(fecha: string): string {
  const [year, month, day] = fecha.split('-');
  return `${day}/${month}/${year}`;
}

export default function MisResultadosPage() {
  const { user } = useAuth();

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [grupos, setGrupos] = useState<GrupoResultados[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function cargar(filtroDesde?: string, filtroHasta?: string) {
    setError(null);
    setLoading(true);
    try {
      const data = await misResultados(filtroDesde, filtroHasta);
      setGrupos(data);
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setError(err.response?.data?.mensaje ?? 'Error al cargar tus resultados.');
      } else {
        setError('Error al cargar tus resultados.');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  function handleFiltrar(e: React.FormEvent) {
    e.preventDefault();
    cargar(desde || undefined, hasta || undefined);
  }

  function handleLimpiar() {
    setDesde('');
    setHasta('');
    cargar();
  }

  async function handleDescargar(item: AnalisisItem) {
    setDownloadingId(item.id);
    try {
      const nombre = nombreArchivoResultado(item.pacienteApellido, item.pacienteNombre, item.fecha);
      await descargarPdf(item.id, nombre);
    } catch {
      setError('No se pudo descargar el PDF.');
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <img src={logo} alt="Logo" className={styles.topbarLogo} />

        <p className={styles.topbarBrand}>Laboratorio Sagrado Corazón</p>
        <UserMenu />
      </header>

      <main className={styles.content}>
        <p className={styles.greeting}>
          {user?.genero === 'M' ? 'Bienvenida' : 'Bienvenido'}{user ? `, ${user.nombre}` : ''}
        </p>

        <form className={styles.filterRow} onSubmit={handleFiltrar}>
          <div className={styles.dateField}>
            <label htmlFor="desde">Desde</label>
            <input id="desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div className={styles.dateField}>
            <label htmlFor="hasta">Hasta</label>
            <input id="hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </div>
          <button className={styles.filterBtn} type="submit" disabled={loading}>
            Filtrar
          </button>
          <button className={styles.clearBtn} type="button" onClick={handleLimpiar} disabled={loading}>
            Limpiar
          </button>
        </form>

        {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

        {loading ? (
          <div className={styles.spinnerWrapper}>
            <div className={styles.spinner} />
            <p className={styles.spinnerText}>Cargando resultados...</p>
          </div>
        ) : (
          grupos.map((grupo) => (
            <div key={grupo.pacienteDni} className={styles.grupoSection}>
              <h2 className={styles.listTitle}>
                {grupo.esPropio ? 'Mis resultados' : `${grupo.pacienteApellido}, ${grupo.pacienteNombre}`}
                <span className={styles.titleBadge}>{grupo.resultados.length}</span>
              </h2>

              {grupo.resultados.length === 0 ? (
                <div className={styles.emptyState}>No se encontraron análisis para el período seleccionado.</div>
              ) : (
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Estudios realizados</th>
                        <th>PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grupo.resultados.map((item) => (
                        <tr key={item.id}>
                          <td>{formatFecha(item.fecha)}</td>
                          <td>{item.estudiosResumen}</td>
                          <td>
                            {item.tienePdf ? (
                              <button
                                className={styles.downloadBtn}
                                onClick={() => handleDescargar(item)}
                                disabled={downloadingId === item.id}
                              >
                                {downloadingId === item.id ? 'Descargando...' : '⬇ Descargar'}
                              </button>
                            ) : (
                              <span className={styles.noPdf}>Sin PDF</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))
        )}
      </main>
    </div>
  );
}
